<script setup lang="ts">
interface FooterProps {
  primary?: boolean;
}

withDefaults(defineProps<FooterProps>(), {
  primary: true,
});

type FooterLink = { title: string; link: string };

const country = useState<string | undefined>('country', () => undefined);

const isRU = computed(() => country.value === 'RU');

const companyName = computed(() => {
  if (isRU.value) {
    return 'ООО Бегемот ЭйАй';
  }
  return 'Where is Everybody Inc.';
});

const address = computed(() => {
  if (isRU.value) {
    return 'г. Москва, ул. Краснопролетарская, д. 7, помещение 4/п';
  }
  return 'Wilmington, DE 19808 \n 251 Little Falls Drive';
});

const currentYear = computed(() => new Date().getFullYear());

const ruLinks: Array<FooterLink> = [
  {
    title: 'Пользовательское соглашение',
    link: '/legal/ru/terms-of-service.pdf',
  },
  {
    title: 'Политика конфиденциальности',
    link: '/legal/ru/privacy-policy.pdf',
  },
  {
    title: 'Публичная оферта',
    link: '/legal/ru/public-offer.pdf',
  },
];

const enLinks: Array<FooterLink> = [
  {
    title: 'Terms of Use',
    link: '/legal/en/terms-of-service.pdf',
  },
  {
    title: 'Privacy Policy',
    link: '/legal/en/privacy-policy.pdf',
  },
  {
    title: 'Cookie Policy',
    link: '/legal/en/cookie-policy.pdf',
  },
];

const links = computed(() => (isRU.value ? ruLinks : enLinks));
</script>

<template>
  <footer
    class="footer-new"
    :class="[primary ? 'footer-new_primary' : 'footer-new_white']"
  >
    <div class="logo footer__logo">
      <NuxtImg
        src="/images/new/logo.svg"
        alt="Логотип Бегемот"
        width="32"
        height="32"
      />
      <div class="logo__brand">Бегемот</div>
    </div>
    <div class="footer__info">
      <div class="footer__address">
        {{ address }}
        <template v-if="isRU">
          <br />
          ОГРН 1247700367452 ИНН 9707030430
        </template>
      </div>
      <div class="footer__copyright">{{ companyName }} © {{ currentYear }}</div>
      <a
        v-if="!isRU"
        class="footer__email"
        :href="`mailto:support@begemot.ai`"
        >support@begemot.ai</a
      >
      <div class="footer_links">
        <a
          v-for="link in links"
          :key="link.title"
          class="link"
          :href="link.link"
          target="_blank"
          rel="noopener noreferrer"
        >
          <span class="text-decoration-underline">{{ link.title }}</span>
        </a>
      </div>
    </div>
  </footer>
</template>

<style lang="scss" scoped>
.footer-new {
  padding: 64px 12px;
  display: flex;
  flex-direction: column;
  gap: 24px;
  border-radius: 32px 32px 0 0;
  position: relative;
  z-index: 1;

  .logo {
    .logo__brand {
      color: $foreground-contrast;
      font-size: 18px;
      font-weight: 800;
      line-height: normal;
    }
  }

  &.footer-new_primary {
    background: #fff;
    margin: 0 20px;

    @include media-breakpoint-down(md) {
      margin: 0;
      background: white;
    }
  }

  &.footer-new_white {
    background: transparent;
  }

  .footer__logo {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 12px;
  }

  .footer__info {
    display: flex;
    flex-direction: column;
    gap: 8px;

    color: $foreground-gray;
    text-align: center;
    font-size: 14px;
    font-weight: 400;
    line-height: 22px;

    .footer__address {
      white-space: pre-line;
    }
  }

  .footer_links {
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin-top: 8px;

    a {
      color: $foreground-theme;
      font-size: 14px;
      font-weight: 400;
      line-height: 1rem;

      &:hover {
        color: rgba($foreground-theme, 0.7);
      }
    }
  }
}
</style>
